import * as React from 'react';

import { cn } from '@/lib/util/cn';

/**
 * Standard page header: a big page title and a slot for action buttons.
 * Clean and quiet — the `supertitle` and `description` props are accepted for
 * back-compat but intentionally NOT rendered (operator feedback: the intro
 * paragraph was noise on every page; the sidebar already gives context).
 */
export function PageHeader({
  supertitle: _supertitle,
  title,
  description: _description,
  subtitle,
  actions,
  className,
}: {
  supertitle?: string;
  title: string;
  description?: string;
  /** One short line under the title (unlike `description`, this renders). */
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{title}</h1>
        {subtitle !== undefined ? <p className="mt-1 text-sm text-fg-subtle">{subtitle}</p> : null}
      </div>
      {actions !== undefined ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/**
 * Placeholder body used by P0-D for the routes that land in P1. Renders
 * a styled "coming-in-phase-X" card so the navigation feels populated.
 */
export function ComingSoon({
  phase,
  title,
  description,
  bullets,
}: {
  phase: string;
  title: string;
  description: string;
  bullets?: string[];
}) {
  return (
    <div className="tile flex flex-col gap-3 p-6">
      <span className="inline-flex w-fit items-center gap-1.5 rounded-sm border border-info/40 bg-info-soft px-2 py-1 text-2xs font-medium text-info">
        Ships in {phase}
      </span>
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <p className="max-w-2xl text-sm text-fg-muted">{description}</p>
      {bullets !== undefined ? (
        <ul className="list-inside list-disc text-sm text-fg-muted">
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
